'use client';

import { useEffect, useState } from 'react';

import { ProductValidatorModal, useData } from '@/components';
import { MenuService, useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { useCookies } from '@/hooks';
import { CookiesNames } from '@/constants';
import { isStoreAgeGateSatisfied } from 'app/_components/AgeGate/utils';

const StoreValidator = () => {
  const {
    line_items,
    next_store_id,
    next_shop_mode,
    next_delivery_address,
    setState,
  } = useEcomStoreSelector([
    'line_items',
    'next_store_id',
    'next_shop_mode',
    'next_delivery_address',
  ]);

  const { ageGate } = useData();
  const { getCookieObject } = useCookies();
  const [outsideProducts, setOutsideProducts] = useState([]);
  const { getInsideStoreProducts, getOutsideProducts } = new MenuService();
  const { setState: setUiState, is_store_validator_modal_open } =
    useUiStoreSelector(['is_store_validator_modal_open']);

  // The per-location gate (if any) must be cleared before we touch the cart or commit.
  const gateSatisfied = isStoreAgeGateSatisfied(
    ageGate?.data,
    next_store_id,
    getCookieObject(CookiesNames._STORE_TERMS),
  );

  const confirmShopChange = async () => {
    let storeProducts = await getInsideStoreProducts(line_items, next_store_id);

    storeProducts = storeProducts?.map(item => {
      const cartItem = line_items.find(s => s.id === item.id);
      return {
        ...item,
        quantity: cartItem.quantity,
        weightVariant: cartItem.weightVariant,
      };
    });

    setState({
      delivery_address: next_delivery_address,
      delivery_details: {
        delivery: next_shop_mode === 'delivery',
        pickup: next_shop_mode === 'pickup',
      },
      treez_store_name: next_store_id,
      line_items: storeProducts,
      next_store_id: null,
      next_delivery_address: null,
      provider_inventory_location_id: null,
    });

    setOutsideProducts([]);
  };

  const confirmKeepShop = () => {
    setState({
      next_shop_mode: null,
      next_store_id: null,
    });
  };

  useEffect(() => {
    if (!next_store_id || !gateSatisfied) {
      setUiState({ is_store_validator_modal_open: false });
      return;
    }

    (async () => {
      const products = await getOutsideProducts(line_items, next_store_id);

      if (products.length) {
        // Items from another store → confirm removal first.
        setOutsideProducts(products);
        setUiState({ is_store_validator_modal_open: true });
      } else {
        // Nothing to remove → switch silently (no empty modal).
        confirmShopChange();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next_store_id, gateSatisfied]);

  return (
    <ProductValidatorModal
      // eslint-disable-next-line max-len
      alertMessage="Be careful! You are about to change the shop. Some of your items will be removed from your shopping cart"
      cancelButtonLabel="Keep item & don’t change shop"
      handleCloseModal={confirmKeepShop}
      handleSubmit={confirmShopChange}
      heading="Do you want to change shops?"
      isModalOpen={is_store_validator_modal_open}
      products={outsideProducts}
      submitButtonLabel="remove item & change shop"
    />
  );
};

export { StoreValidator };
